/**
 * The files this tab uploaded, kept as object URLs, so the creator's own new upload shows from the file already in the
 * browser and nothing is downloaded back. Keyed by the address the upload answered, without its query or fragment, so
 * the same address read back with a cache version (?v=, ?t=) finds it too.
 *
 * Only what the web app shows itself goes through `shownUrl`: images and videos. An address that is saved, or handed to
 * the renderer, stays the one the upload answered: an object URL works in this tab alone. So a model or a sidecar (its
 * textures' bytes, its JSON), which only the renderer loads, keeps no copy here.
 */

/** The most kept at once, by count and by size; the oldest are let go first, so a long session doesn't hold every file it ever sent. */
export const MAX_COPIES = 48;
export const MAX_COPY_BYTES = 256 * 1024 * 1024;

/** The media shownUrl shows: an image or a video, by its type. */
const isShown = (type: string) => /^(image|video)\//i.test(type.trim());

const copies = new Map<string, { url: string; size: number }>();
let keptBytes = 0;

const keyOf = (address: string) => address.split(/[?#]/, 1)[0];

function letGo(key: string): void {
  const copy = copies.get(key);
  if (!copy) return;
  copies.delete(key);
  keptBytes -= copy.size;
  URL.revokeObjectURL(copy.url);
}

/**
 * Keeps `file` as the local copy of the uploaded file at `address`, when it is media the web app shows; `type` is the
 * type it was stored with (the file's own when not given). One bigger than the whole limit is not kept.
 */
export function keepLocalCopy(address: string, file: Blob, type: string = file.type): void {
  if (!address || typeof URL.createObjectURL !== 'function') return;
  const key = keyOf(address);
  letGo(key);
  if (!isShown(type) || file.size > MAX_COPY_BYTES) return;
  copies.set(key, { url: URL.createObjectURL(file), size: file.size });
  keptBytes += file.size;
  while (copies.size > MAX_COPIES || keptBytes > MAX_COPY_BYTES) letGo(copies.keys().next().value as string);
}

/** The address to show a file at: this tab's own copy when it uploaded the file, else the address as it is. */
export function shownUrl<T extends string | null | undefined>(address: T): T | string {
  if (!address) return address;
  return copies.get(keyOf(address))?.url ?? address;
}
